import {
   type AnalyticsItem,
   type AnalyticsItemsEvent,
   type CurrencyCode,
   toUSD,
   trackAnalyticsCustomEvent,
} from '@ifixit/analytics';
import { getProductIdFromGlobalId, getVariantIdFromVariantURI } from '@ifixit/helpers';
import { AddToCartInput } from '../hooks/cart/use-add-to-cart';
import type { CartLineItem } from '../types';

const ADD_TO_CART_EVENT = 'Add to Cart';

export function trackAnalyticsShopifyAddToCart({ lines, analytics }: AddToCartInput) {
   const itemcodes =
      analytics.descriptor === 'Frequently Bought Together'
         ? `${analytics.currentItemcode}/${lines[0].itemcode}`
         : lines.map(line => line.itemcode).join(', ');
   const totalPriceUSD = lines.reduce((sum, line) => {
      const lineUSD = toUSD(Number(line.price.amount), line.price.currencyCode as CurrencyCode);
      const linePrice = line.quantity * lineUSD;
      return sum + linePrice;
   }, 0);
   trackAnalyticsCustomEvent({
      eventCategory: ADD_TO_CART_EVENT,
      eventAction: `${ADD_TO_CART_EVENT} - ${analytics.descriptor}`,
      eventName: itemcodes,
      eventValue: totalPriceUSD,
   });
}

export function convertAddToCartInputToAnalyticsItemEvent(
   input: AddToCartInput,
   shopifyCartId: string | undefined
): AnalyticsItemsEvent {
   return {
      items: convertCartLineItemsToAnalyticsItem(input.lines),
      value: input.lines.reduce((acc, item) => acc + Number(item.price.amount), 0),
      currency: input.lines[0]?.price.currencyCode,
      localeCode: input.analytics.localeCode,
      shopifyCartId,
   };
}

export function convertCartLineItemsToAnalyticsItem(items: CartLineItem[]): AnalyticsItem[] {
   return items.map(item => ({
      item_id: item.itemcode,
      item_name: item.internalDisplayName ?? `${item.name} - ${item.variantTitle}`,
      item_variant: getVariantIdFromVariantURI(item.shopifyVariantId),
      numericProductId: getProductIdFromGlobalId(item.shopifyProductId),
      quantity: item.quantity,
      price: Number(item.price.amount),
      categories: item.categories ?? [],
      fulfiller: item.fulfiller,
      oemPartnership: item.oemPartnership?.code,
   }));
}
